import { msg } from "@lingui/core/macro";
import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { Factory, FolderOpen, Pencil, Trash2 } from "lucide-solid";
import {
  addBomComponentMutation,
  addPurchaseReturnLineMutation,
  applyLandedCostMutation,
  coerceLandedCostState,
  coerceManufacturingState,
  coercePurchaseReturnState,
  confirmPurchaseReturnMutation,
  createBomMutation,
  createLandedCostMutation,
  createManufacturingOrderMutation,
  createPurchaseReturnMutation,
  deleteBomComponentMutation,
  deleteBomMutation,
  deleteManufacturingOrderMutation,
  fromMinorQty,
  getBomComponentsQuery,
  getBomsQuery,
  getLandedCostsQuery,
  getLocationsQuery,
  getManufacturingOrdersQuery,
  getProductsQuery,
  getPurchaseReturnLinesQuery,
  getPurchaseReturnsQuery,
  getReceiptsQuery,
  parseQtyInput,
  produceManufacturingOrderMutation,
  QTY_SCALE,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { useResetOnOrgSwitch } from "@/features/inventory/use-reset-on-org-switch";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageHeader } from "@/components/ui/page-header";
import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { SwipeRow } from "@/components/ui/swipe-row";
import type {
  BillOfMaterialsRecord,
  BomComponentRecord,
  LandedCostRecord,
  LocationRecord,
  ManufacturingOrderRecord,
  ProductRecord,
  PurchaseReturnLineRecord,
  PurchaseReturnRecord,
  StockPickingRecord,
} from "@stockpilot/powersync";

function ManufacturingPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/u/$u/manufacturing" });
  const navigate = useNavigate();

  // ─── Estado de formularios ──────────────────────────────────────────────
  const [bomProduct, setBomProduct] = createSignal("");
  const [compProduct, setCompProduct] = createSignal("");
  const [compQty, setCompQty] = createSignal("");

  const [moProduct, setMoProduct] = createSignal("");
  const [moLocation, setMoLocation] = createSignal("");
  const [moQty, setMoQty] = createSignal("");

  const [lcReceipt, setLcReceipt] = createSignal("");
  const [lcAmount, setLcAmount] = createSignal("");

  const [retLocation, setRetLocation] = createSignal("");
  const [retProduct, setRetProduct] = createSignal("");
  const [retQty, setRetQty] = createSignal("");

  // El BoM y la devolución abiertos son deep-linkables y coexisten en la misma
  // URL (`?bom=<id>` y/o `?return=<id>`): la URL es la fuente de verdad. Cada
  // setter escribe SOLO su param y preserva el otro, de modo que se puede compartir
  // un BoM, una devolución, o ambos abiertos a la vez. Idempotente al recargar.
  const openBomId = (): string | null => search().bom ?? null;
  const setOpenBomId = (id: string | null) => {
    void navigate({
      to: "/manufacturing",
      search: (prev) => ({ ...prev, bom: id ?? undefined }),
      replace: true,
    });
  };

  const openReturnId = (): string | null => search().return ?? null;
  const setOpenReturnId = (id: string | null) => {
    void navigate({
      to: "/manufacturing",
      search: (prev) => ({ ...prev, return: id ?? undefined }),
      replace: true,
    });
  };

  // Guard del flush diferido del router: si el boot perdió los params del
  // deep-link inicial, los re-aplicamos síncronamente desde el snapshot.
  onMount(() => {
    const initialBom = takeInitialSearchParam("bom", "/manufacturing");
    if (initialBom && !openBomId()) {
      setOpenBomId(initialBom);
    }
    const initialReturn = takeInitialSearchParam("return", "/manufacturing");
    if (initialReturn && !openReturnId()) {
      setOpenReturnId(initialReturn);
    }
  });

  // Aislamiento multi-organización: al cambiar de empresa, el BoM / la
  // devolución abiertos pertenecen a la org anterior → descartamos ambos.
  useResetOnOrgSwitch(() => {
    setOpenBomId(null);
    setOpenReturnId(null);
  });

  // ─── Queries ─────────────────────────────────────────────────────────────
  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const boms = createPowerSyncQuery<BillOfMaterialsRecord>({
    queryKey: () => queryKeys.inventory.boms(orgId()),
    queryFn: () => getBomsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const openComponents = createPowerSyncQuery<BomComponentRecord>({
    queryKey: () => queryKeys.inventory.bomComponents(openBomId() ?? undefined),
    queryFn: () => getBomComponentsQuery(drizzle()!, openBomId()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && openBomId()),
  });

  const orders = createPowerSyncQuery<ManufacturingOrderRecord>({
    queryKey: () => queryKeys.inventory.manufacturingOrders(orgId()),
    queryFn: () => getManufacturingOrdersQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const receipts = createPowerSyncQuery<StockPickingRecord>({
    queryKey: () => queryKeys.inventory.receipts(orgId()),
    queryFn: () => getReceiptsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const landedCosts = createPowerSyncQuery<LandedCostRecord>({
    queryKey: () => queryKeys.inventory.landedCosts(orgId()),
    queryFn: () => getLandedCostsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const returns = createPowerSyncQuery<PurchaseReturnRecord>({
    queryKey: () => queryKeys.inventory.purchaseReturns(orgId()),
    queryFn: () => getPurchaseReturnsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const openReturnLines = createPowerSyncQuery<PurchaseReturnLineRecord>({
    queryKey: () => queryKeys.inventory.purchaseReturnLines(openReturnId() ?? undefined),
    queryFn: () => getPurchaseReturnLinesQuery(drizzle()!, openReturnId()!),
    db: () => db(),
    enabled: () => Boolean(drizzle() && openReturnId()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string | null) => (id ? (map.get(id) ?? id) : "—");
  });

  const openBom = createMemo(() => (boms.data ?? []).find((b) => b.id === openBomId()));
  const openReturn = createMemo(() => (returns.data ?? []).find((r) => r.id === openReturnId()));

  // Resuelve el BoM / la devolución deep-linkeados: presente / cargando /
  // no-encontrado / offline. Evita el panel vacío silencioso cuando la entidad no
  // existe, fue borrada o el usuario perdió acceso. Nunca deja un loader colgado.
  const bomResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(openBom()),
    isPending: () => boms.isLoading,
  });
  const returnResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(openReturn()),
    isPending: () => returns.isLoading,
  });

  const mfgStateLabel = (state: string | null) =>
    coerceManufacturingState(state) === "done" ? t(msg`Done`) : t(msg`Draft`);
  const lcStateLabel = (state: string | null) =>
    coerceLandedCostState(state) === "done" ? t(msg`Applied`) : t(msg`Draft`);
  const retStateLabel = (state: string | null) =>
    coercePurchaseReturnState(state) === "done" ? t(msg`Returned`) : t(msg`Draft`);

  // ─── Manejadores: BoM ──────────────────────────────────────────────────────
  async function handleCreateBom(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d || !bomProduct()) {
      return;
    }
    const id = await createBomMutation(d, orgId(), { productId: bomProduct() });
    setBomProduct("");
    setOpenBomId(id);
  }

  async function handleAddComponent() {
    const d = drizzle();
    const bom = openBom();
    const qty = parseQtyInput(compQty());
    if (!d || !bom || !compProduct() || qty === null || qty <= 0) {
      return;
    }
    await addBomComponentMutation(d, orgId(), bom.id, compProduct(), qty);
    setCompProduct("");
    setCompQty("");
  }

  async function handleDeleteComponent(id: string) {
    const d = drizzle();
    if (d) {
      await deleteBomComponentMutation(d, id);
    }
  }

  async function handleDeleteBom(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteBomMutation(d, id);
    if (openBomId() === id) {
      setOpenBomId(null);
    }
  }

  // ─── Manejadores: órdenes de fabricación ────────────────────────────────────
  async function handleCreateOrder(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const qty = parseQtyInput(moQty());
    if (!d || !moProduct() || !moLocation() || qty === null || qty <= 0) {
      return;
    }
    // BoM del producto a fabricar (si existe) → componentes a consumir.
    const bom = (boms.data ?? []).find((b) => b.product_id === moProduct());
    await createManufacturingOrderMutation(d, orgId(), {
      bomId: bom?.id ?? null,
      productId: moProduct(),
      qty,
      locationId: moLocation(),
    });
    setMoProduct("");
    setMoQty("");
  }

  async function handleProduce(order: ManufacturingOrderRecord) {
    const d = drizzle();
    if (!d) {
      return;
    }
    const bomId = order.bom_id;
    const bom = bomId ? (boms.data ?? []).find((b) => b.id === bomId) : undefined;
    let components: { product_id: string | null; qty: number | null }[] = [];
    if (bomId) {
      const rows = await getBomComponentsQuery(d, bomId);
      components = rows.map((c) => ({ product_id: c.product_id, qty: c.qty }));
    }
    await produceManufacturingOrderMutation(
      d,
      orgId(),
      {
        id: order.id,
        state: order.state,
        product_id: order.product_id,
        qty: order.qty,
        bom_qty: bom?.qty ?? QTY_SCALE,
        location_id: order.location_id,
      },
      components,
    );
  }

  async function handleDeleteOrder(id: string) {
    const d = drizzle();
    if (d) {
      await deleteManufacturingOrderMutation(d, id);
    }
  }

  // ─── Manejadores: landed costs ──────────────────────────────────────────────
  async function handleCreateLandedCost(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const amount = parseQtyInput(lcAmount());
    if (!d || !lcReceipt() || amount === null || amount <= 0) {
      return;
    }
    await createLandedCostMutation(d, orgId(), { pickingId: lcReceipt(), amount });
    setLcAmount("");
  }

  async function handleApplyLandedCost(lc: LandedCostRecord) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await applyLandedCostMutation(d, orgId(), {
      id: lc.id,
      state: lc.state,
      picking_id: lc.picking_id,
      amount: lc.amount,
    });
  }

  // ─── Manejadores: devoluciones ──────────────────────────────────────────────
  async function handleCreateReturn(e: Event) {
    e.preventDefault();
    const d = drizzle();
    if (!d || !retLocation()) {
      return;
    }
    const id = await createPurchaseReturnMutation(d, orgId(), { srcLocationId: retLocation() });
    setOpenReturnId(id);
  }

  async function handleAddReturnLine() {
    const d = drizzle();
    const ret = openReturn();
    const qty = parseQtyInput(retQty());
    if (!d || !ret || !retProduct() || qty === null || qty <= 0) {
      return;
    }
    await addPurchaseReturnLineMutation(d, orgId(), ret.id, retProduct(), qty);
    setRetProduct("");
    setRetQty("");
  }

  async function handleConfirmReturn() {
    const d = drizzle();
    const ret = openReturn();
    if (!d || !ret) {
      return;
    }
    const lines = (openReturnLines.data ?? []).map((l) => ({
      product_id: l.product_id,
      qty: l.qty,
    }));
    await confirmPurchaseReturnMutation(
      d,
      orgId(),
      {
        id: ret.id,
        state: ret.state,
        src_location_id: ret.src_location_id,
        warehouse_id: ret.warehouse_id,
      },
      lines,
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Manufacturing`)}
        description={t(
          msg`Bills of materials, manufacturing orders, landed costs and supplier returns.`,
        )}
        icon={Factory}
      />

      {/* ─── Listas de materiales (BoM) ─── */}
      <h2 class="mt-6 text-lg font-semibold">{t(msg`Bills of materials`)}</h2>
      <form
        class="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateBom}
        data-testid="bom-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Finished product`)}</label>
          <Select
            class="w-52"
            value={bomProduct()}
            onChange={(v) => setBomProduct(v)}
            data-testid="bom-product-select"
            aria-label={t(msg`Finished product`)}
            placeholder={t(msg`Select product`)}
            options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="bom-create-button">
          {t(msg`Create BoM`)}
        </Button>
      </form>

      {/* Deep-link no resoluble: la URL pide un BoM que no existe / sin acceso. */}
      <Show when={openBomId() && bomResolution() !== "ready" && bomResolution() !== "loading"}>
        <div class="mt-3" data-testid="bom-not-found">
          <NotFoundState
            variant={bomResolution() === "offline" ? "offline" : "not-found"}
            title={bomResolution() === "offline" ? undefined : t(msg`Bill of materials not found`)}
            description={
              bomResolution() === "offline"
                ? undefined
                : t(msg`This BoM may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Close`),
              onClick: () => {
                setOpenBomId(null);
              },
            }}
          />
        </div>
      </Show>

      <Show when={openBom()}>
        {(bom) => (
          <div class="mt-3 rounded-md border border-border p-4" data-testid="bom-editor">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold">
                {t(msg`Components of`)} {productName()(bom().product_id)}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setOpenBomId(null)}>
                {t(msg`Close`)}
              </Button>
            </div>
            <div class="mt-3 flex flex-wrap items-end gap-2">
              <Select
                class="w-48"
                value={compProduct()}
                onChange={(v) => setCompProduct(v)}
                data-testid="bom-component-product-select"
                aria-label={t(msg`Component`)}
                placeholder={t(msg`Select component`)}
                options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
              />
              <input
                class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                placeholder={t(msg`Quantity`)}
                inputmode="decimal"
                value={compQty()}
                onInput={(e) => setCompQty(e.currentTarget.value)}
                data-testid="bom-component-qty-input"
              />
              <Button
                onClick={() => void handleAddComponent()}
                disabled={!drizzle()}
                data-testid="bom-component-add-button"
              >
                {t(msg`Add component`)}
              </Button>
            </div>
            <ul class="mt-3 divide-y divide-border/50" data-testid="bom-component-list">
              <For each={openComponents.data ?? []}>
                {(c) => (
                  <li
                    class="flex items-center justify-between py-2 text-sm"
                    data-testid="bom-component-row"
                    data-product-id={c.product_id ?? ""}
                  >
                    <span>
                      {productName()(c.product_id)}{" "}
                      <span class="text-muted-foreground tabular-nums">
                        × {fromMinorQty(c.qty ?? 0)}
                      </span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDeleteComponent(c.id)}
                    >
                      {t(msg`Remove`)}
                    </Button>
                  </li>
                )}
              </For>
            </ul>
          </div>
        )}
      </Show>

      <ul
        class="mt-3 divide-y divide-border rounded-md border border-border"
        data-testid="bom-list"
      >
        <For each={boms.data ?? []}>
          {(b) => (
            <li data-testid="bom-row" data-bom-id={b.id}>
              <SwipeRow
                disabled={openBomId() === b.id}
                right={{
                  icon: Pencil,
                  label: t(msg`Edit`),
                  tone: "primary",
                  onCommit: () => setOpenBomId(b.id),
                }}
                left={{
                  icon: Trash2,
                  label: t(msg`Delete`),
                  tone: "danger",
                  exits: true,
                  onCommit: () => void handleDeleteBom(b.id),
                }}
              >
                <div class="flex items-center gap-3 px-4 py-3">
                  <Factory class="h-5 w-5 text-primary shrink-0" />
                  <span class="flex-1 font-medium">{productName()(b.product_id)}</span>
                  <Button variant="ghost" size="sm" onClick={() => setOpenBomId(b.id)}>
                    {t(msg`Edit`)}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void handleDeleteBom(b.id)}>
                    {t(msg`Delete`)}
                  </Button>
                </div>
              </SwipeRow>
            </li>
          )}
        </For>
      </ul>

      {/* ─── Órdenes de fabricación ─── */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Manufacturing orders`)}</h2>
      <form
        class="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateOrder}
        data-testid="mo-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Product to make`)}</label>
          <Select
            class="w-48"
            value={moProduct()}
            onChange={(v) => setMoProduct(v)}
            data-testid="mo-product-select"
            aria-label={t(msg`Product to make`)}
            placeholder={t(msg`Select product`)}
            options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Location`)}</label>
          <Select
            class="w-44"
            value={moLocation()}
            onChange={(v) => setMoLocation(v)}
            data-testid="mo-location-select"
            aria-label={t(msg`Location`)}
            placeholder={t(msg`Select location`)}
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Quantity`)}</label>
          <input
            class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder="0"
            inputmode="decimal"
            value={moQty()}
            onInput={(e) => setMoQty(e.currentTarget.value)}
            data-testid="mo-qty-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="mo-create-button">
          {t(msg`Create order`)}
        </Button>
      </form>

      <div class="mt-3" data-testid="mo-list">
        <Show
          when={(orders.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={Factory}
              title={t(msg`No manufacturing orders yet`)}
              description={t(
                msg`Create an order to consume components and produce a finished good.`,
              )}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={orders.data ?? []}>
              {(o) => (
                <li
                  data-testid="mo-row"
                  data-mo-id={o.id}
                  data-mo-state={o.state ?? ""}
                  data-produced-cost={o.produced_cost ?? 0}
                >
                  <SwipeRow
                    left={
                      coerceManufacturingState(o.state) === "draft"
                        ? {
                            icon: Trash2,
                            label: t(msg`Delete`),
                            tone: "danger",
                            exits: true,
                            onCommit: () => void handleDeleteOrder(o.id),
                          }
                        : undefined
                    }
                  >
                    <div class="flex items-center gap-3 px-4 py-3">
                      <Factory class="h-5 w-5 text-primary shrink-0" />
                      <div class="flex-1 min-w-0">
                        <p class="font-medium">
                          {productName()(o.product_id)}{" "}
                          <span class="text-muted-foreground tabular-nums">
                            × {fromMinorQty(o.qty ?? 0)}
                          </span>
                        </p>
                        <Show when={coerceManufacturingState(o.state) === "done"}>
                          <p class="text-xs text-muted-foreground tabular-nums">
                            {t(msg`Unit cost`)}:{" "}
                            <span data-testid="mo-row-cost">
                              {fromMinorQty(o.produced_cost ?? 0)}
                            </span>
                          </p>
                        </Show>
                      </div>
                      <span
                        class="rounded-full border border-border px-2 py-0.5 text-xs"
                        data-testid="mo-row-state"
                      >
                        {mfgStateLabel(o.state)}
                      </span>
                      <Show when={coerceManufacturingState(o.state) === "draft"}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleProduce(o)}
                          disabled={!drizzle()}
                          data-testid="mo-produce-button"
                        >
                          {t(msg`Produce`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleDeleteOrder(o.id)}
                        >
                          {t(msg`Delete`)}
                        </Button>
                      </Show>
                    </div>
                  </SwipeRow>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>

      {/* ─── Landed costs ─── */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Landed costs`)}</h2>
      <form
        class="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateLandedCost}
        data-testid="lc-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Receipt`)}</label>
          <Select
            class="w-56"
            value={lcReceipt()}
            onChange={(v) => setLcReceipt(v)}
            data-testid="lc-receipt-select"
            aria-label={t(msg`Receipt`)}
            placeholder={t(msg`Select receipt`)}
            options={(receipts.data ?? []).map((r) => ({
              value: r.id,
              label: r.reference ?? r.id.slice(0, 8),
            }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Extra cost`)}</label>
          <input
            class="w-32 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder="0"
            inputmode="decimal"
            value={lcAmount()}
            onInput={(e) => setLcAmount(e.currentTarget.value)}
            data-testid="lc-amount-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="lc-create-button">
          {t(msg`Add landed cost`)}
        </Button>
      </form>

      <ul class="mt-3 divide-y divide-border rounded-md border border-border" data-testid="lc-list">
        <For each={landedCosts.data ?? []}>
          {(lc) => (
            <li
              class="flex items-center gap-3 px-4 py-3"
              data-testid="lc-row"
              data-lc-id={lc.id}
              data-lc-state={lc.state ?? ""}
            >
              <div class="flex-1 min-w-0">
                <p class="font-medium tabular-nums">{fromMinorQty(lc.amount ?? 0)}</p>
              </div>
              <span
                class="rounded-full border border-border px-2 py-0.5 text-xs"
                data-testid="lc-row-state"
              >
                {lcStateLabel(lc.state)}
              </span>
              <Show when={coerceLandedCostState(lc.state) === "draft"}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleApplyLandedCost(lc)}
                  disabled={!drizzle()}
                  data-testid="lc-apply-button"
                >
                  {t(msg`Apply`)}
                </Button>
              </Show>
            </li>
          )}
        </For>
      </ul>

      {/* ─── Devoluciones a proveedor ─── */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Supplier returns`)}</h2>
      <form
        class="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateReturn}
        data-testid="ret-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Source location`)}</label>
          <Select
            class="w-44"
            value={retLocation()}
            onChange={(v) => setRetLocation(v)}
            data-testid="ret-location-select"
            aria-label={t(msg`Source location`)}
            placeholder={t(msg`Select location`)}
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="ret-create-button">
          {t(msg`Create return`)}
        </Button>
      </form>

      {/* Deep-link no resoluble: la URL pide una devolución que no existe / sin acceso. */}
      <Show
        when={openReturnId() && returnResolution() !== "ready" && returnResolution() !== "loading"}
      >
        <div class="mt-3" data-testid="ret-not-found">
          <NotFoundState
            variant={returnResolution() === "offline" ? "offline" : "not-found"}
            title={returnResolution() === "offline" ? undefined : t(msg`Return not found`)}
            description={
              returnResolution() === "offline"
                ? undefined
                : t(msg`This return may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Close`),
              onClick: () => {
                setOpenReturnId(null);
              },
            }}
          />
        </div>
      </Show>

      <Show when={openReturn()}>
        {(ret) => (
          <div
            class="mt-3 rounded-md border border-border p-4"
            data-testid="ret-editor"
            data-ret-id={ret().id}
            data-ret-state={ret().state ?? ""}
          >
            <div class="flex items-center justify-between">
              <h3 class="font-semibold">{t(msg`Return lines`)}</h3>
              <span class="rounded-full border border-border px-2 py-0.5 text-xs">
                {retStateLabel(ret().state)}
              </span>
            </div>
            <Show when={coercePurchaseReturnState(ret().state) === "draft"}>
              <div class="mt-3 flex flex-wrap items-end gap-2">
                <Select
                  class="w-48"
                  value={retProduct()}
                  onChange={(v) => setRetProduct(v)}
                  data-testid="ret-line-product-select"
                  aria-label={t(msg`Product`)}
                  placeholder={t(msg`Select product`)}
                  options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                />
                <input
                  class="w-28 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
                  placeholder={t(msg`Quantity`)}
                  inputmode="decimal"
                  value={retQty()}
                  onInput={(e) => setRetQty(e.currentTarget.value)}
                  data-testid="ret-line-qty-input"
                />
                <Button
                  onClick={() => void handleAddReturnLine()}
                  disabled={!drizzle()}
                  data-testid="ret-line-add-button"
                >
                  {t(msg`Add line`)}
                </Button>
              </div>
            </Show>
            <ul class="mt-3 divide-y divide-border/50" data-testid="ret-line-list">
              <For each={openReturnLines.data ?? []}>
                {(l) => (
                  <li
                    class="flex items-center justify-between py-2 text-sm"
                    data-testid="ret-line-row"
                  >
                    <span>
                      {productName()(l.product_id)}{" "}
                      <span class="text-muted-foreground tabular-nums">
                        × {fromMinorQty(l.qty ?? 0)}
                      </span>
                    </span>
                  </li>
                )}
              </For>
            </ul>
            <div class="mt-3 flex gap-2">
              <Show when={coercePurchaseReturnState(ret().state) === "draft"}>
                <Button
                  onClick={() => void handleConfirmReturn()}
                  disabled={!drizzle()}
                  data-testid="ret-confirm-button"
                >
                  {t(msg`Confirm return`)}
                </Button>
              </Show>
              <Button variant="ghost" onClick={() => setOpenReturnId(null)}>
                {t(msg`Close`)}
              </Button>
            </div>
          </div>
        )}
      </Show>

      <ul
        class="mt-3 divide-y divide-border rounded-md border border-border"
        data-testid="ret-list"
      >
        <For each={returns.data ?? []}>
          {(r) => (
            <li data-testid="ret-row" data-ret-id={r.id} data-ret-state={r.state ?? ""}>
              <SwipeRow
                right={{
                  icon: FolderOpen,
                  label: t(msg`Open`),
                  tone: "primary",
                  onCommit: () => setOpenReturnId(r.id),
                }}
              >
                <div class="flex items-center gap-3 px-4 py-3">
                  <span class="flex-1 font-medium">{t(msg`Return`)}</span>
                  <span
                    class="rounded-full border border-border px-2 py-0.5 text-xs"
                    data-testid="ret-row-state"
                  >
                    {retStateLabel(r.state)}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setOpenReturnId(r.id)}>
                    {t(msg`Open`)}
                  </Button>
                </div>
              </SwipeRow>
            </li>
          )}
        </For>
      </ul>
    </PageContainer>
  );
}

export const manufacturingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/manufacturing",
  validateSearch: (search: Record<string, unknown>): { bom?: string; return?: string } => ({
    bom: typeof search.bom === "string" && search.bom.trim() !== "" ? search.bom : undefined,
    return:
      typeof search.return === "string" && search.return.trim() !== "" ? search.return : undefined,
  }),
  component: ManufacturingPage,
});
